import type { EncounterType } from '@/entities/encounter/types'
import { AppError } from '@/shared/lib/appError'
import { toRepositoryError } from '@/shared/repositories/common/repositoryError'
import { supabase } from '@/shared/supabase/client'

export interface RepoEncounterRecord {
  id: string
  patientId: string
  createdByUserId: string
  type: EncounterType
  occurredAt: string
  complaint: string | null
  description: string
  createdAt: string
  principioAtivo?: string | null
  concentracao?: string | null
  unidade?: string | null
  descricaoLivre?: string | null
}

export interface CreateEncounterPayload {
  patientId: string
  type: EncounterType
  occurredAt: string
  complaint?: string | null
  description: string
  principioAtivo?: string | null
  concentracao?: string | null
  unidade?: string | null
  descricaoLivre?: string | null
}

const getCurrentUserId = async (): Promise<string> => {
  const { data, error } = await supabase.auth.getUser()
  if (error) {
    throw toRepositoryError(error, 'Não foi possível validar a sessão do usuário.', 401)
  }
  if (!data.user) {
    throw new AppError('Usuário não autenticado.', { status: 401, code: 'UNAUTHORIZED' })
  }
  return data.user.id
}

const normalizeEncounterType = (value: unknown): EncounterType =>
  value === 'retorno' || value === 'aplicacao' ? value : 'consulta'

const toRepoEncounter = (row: {
  id: string
  patient_id: string
  created_by_user_id: string
  type: string
  occurred_at: string
  complaint?: string | null
  description: string
  created_at: string
  principio_ativo?: string | null
  concentracao?: string | null
  unidade?: string | null
  descricao_livre?: string | null
}): RepoEncounterRecord => ({
  id: row.id,
  patientId: row.patient_id,
  createdByUserId: row.created_by_user_id,
  type: normalizeEncounterType(row.type),
  occurredAt: row.occurred_at,
  complaint: row.complaint?.trim() || null,
  description: row.description,
  createdAt: row.created_at,
  principioAtivo: row.principio_ativo?.trim() || null,
  concentracao: row.concentracao?.trim() || null,
  unidade: row.unidade?.trim() || null,
  descricaoLivre: row.descricao_livre?.trim() || null,
})

export const encountersRepository = {
  listByPatient: async (patientId: string): Promise<{ encounters: RepoEncounterRecord[] }> => {
    try {
      await getCurrentUserId()
      const { data, error } = await supabase
        .from('encounters')
        .select('*')
        .eq('patient_id', patientId)
        .order('occurred_at', { ascending: false })
        .order('created_at', { ascending: false })

      if (error) {
        throw toRepositoryError(error, 'Não foi possível listar atendimentos do paciente.')
      }

      return {
        encounters: (data ?? []).map(toRepoEncounter),
      }
    } catch (error) {
      throw toRepositoryError(error, 'Não foi possível listar atendimentos do paciente.')
    }
  },

  create: async (
    payload: CreateEncounterPayload,
  ): Promise<{ encounter: RepoEncounterRecord }> => {
    try {
      const userId = await getCurrentUserId()
      const normalizedDate = new Date(payload.occurredAt)
      if (Number.isNaN(normalizedDate.getTime())) {
        throw new AppError('Data/hora do atendimento inválida.', {
          status: 422,
          code: 'VALIDATION',
        })
      }

      const description = payload.description?.trim() ?? ''
      const complaint = payload.complaint?.trim() || null
      const principioAtivo = payload.principioAtivo?.trim() || null
      const concentracao = payload.concentracao?.trim() || null
      const unidade = payload.unidade?.trim() || null
      const descricaoLivre = payload.descricaoLivre?.trim() || null

      const isAplicacao = payload.type === 'aplicacao'
      if (isAplicacao) {
        const hasAplicacao = principioAtivo || concentracao || unidade || descricaoLivre
        const hasClassic = complaint || description
        if (!hasAplicacao && !hasClassic) {
          throw new AppError(
            'Para aplicação, informe ao menos: princípio ativo, concentração, unidade, descrição livre, queixa ou conduta.',
            { status: 422, code: 'VALIDATION' },
          )
        }
      } else {
        if (!description) {
          throw new AppError('Descrição da conduta clínica é obrigatória.', {
            status: 422,
            code: 'VALIDATION',
          })
        }
      }

      const descriptionValue = description || (isAplicacao ? 'Aplicação' : '')
      if (!descriptionValue.trim()) {
        throw new AppError('Descrição da conduta clínica é obrigatória.', {
          status: 422,
          code: 'VALIDATION',
        })
      }

      const { data, error } = await supabase
        .from('encounters')
        .insert({
          patient_id: payload.patientId,
          created_by_user_id: userId,
          type: payload.type,
          occurred_at: normalizedDate.toISOString(),
          complaint,
          description: descriptionValue,
          principio_ativo: principioAtivo,
          concentracao: concentracao,
          unidade: unidade,
          descricao_livre: descricaoLivre,
        })
        .select('*')
        .single()

      if (error) {
        throw toRepositoryError(error, 'Não foi possível registrar atendimento.')
      }

      return {
        encounter: toRepoEncounter(data),
      }
    } catch (error) {
      throw toRepositoryError(error, 'Não foi possível registrar atendimento.')
    }
  },
}

